// 🎃 10-07 pumpkins (P7): the carved pumpkins sitting IN a porch picture (the carving sheet's "The porch
// tonight", and the porch's /look picture). Sprites only exist where the painting has a box: the spots are
// % of the painting (utils/roomPeek.PUMPKIN_SPOTS, the mockup's, on the autumn porch takes, which share one
// layout). `porch` = the backend's porch read: spots[i] takes spot i (residents first, then visitors, by first
// carve); the viewer's own pumpkin (`mine`) sits front and center in their own view. Lit from the rider's
// glow ('off' by day, 'lit' 7 PM to dawn, 'all' on Oct 31: every one lit, brighter); steady under reduced motion.
// ⚠️ Class names carry no theme substring (card · panel · button · btn · tag · chip · input · primary): the
// House theme styles by substring, and its blanket button rule sets overflow: hidden (inline styles win).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { pumpkinSpotsFor, pumpkinYoursFor } from '../../utils/roomPeek';
import { drawPumpkin, loadPainting, flickAt } from '../../utils/pumpkinDraw';

const HUMAN = '#f5c451';
const AI = '#62d8ea';
const reducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
};

/** Which pumpkin sits where: [{ pk, spot, mine }]. Pure (exported for the gate). The viewer's own pumpkin
 *  takes YOURS (not its list spot); the rest fill the room's spots in the porch read's order. */
export function placePumpkins(porch, roomId = 'porch') {
  const spots = pumpkinSpotsFor(roomId);
  const yours = pumpkinYoursFor(roomId);
  const list = (porch && Array.isArray(porch.spots)) ? porch.spots : [];
  const mineId = porch && porch.mine && porch.mine.hidden !== true ? porch.mine.id : null;
  const out = [];
  let k = 0;
  for (const pk of list) {
    if (!pk || !Array.isArray(pk.cuts)) continue;
    if (mineId && pk.id === mineId) continue;
    if (k >= spots.length) break;
    out.push({ pk, spot: spots[k], mine: false });
    k += 1;
  }
  if (mineId && yours) {
    const pk = list.find((p) => p && p.id === mineId) || porch.mine;
    if (pk && Array.isArray(pk.cuts)) out.push({ pk, spot: yours, mine: true });
  }
  return out;
}

/** The pumpkins this view could NOT draw (the "See all" list starts with them). */
export function undrawnPumpkins(porch, placed) {
  const drawn = new Set(placed.map((p) => p.pk.id));
  return ((porch && Array.isArray(porch.spots)) ? porch.spots : []).filter((pk) => pk && !drawn.has(pk.id));
}

function Sprite({ item, index, glow, onOpen }) {
  const ref = useRef(null);
  const btnRef = useRef(null);
  useEffect(() => {
    const lit = glow === 'lit' || glow === 'all';
    const bright = glow === 'all';
    const reduce = reducedMotion();
    let raf = 0;
    let alive = true;
    let last = 0;
    const size = () => (btnRef.current && btnRef.current.clientWidth) || 60;
    const paint = (t) => {
      raf = 0;
      const c = ref.current;
      if (!alive || !c) return;
      if (!lit || reduce) {
        drawPumpkin(c, item.pk, { lit, bright, flick: 1, size: size() });
        return; // nothing flickers: one paint
      }
      if (t - last > 90) { // the mockup's ~11 fps candle
        drawPumpkin(c, item.pk, { lit, bright, flick: flickAt(t, index, false), size: size() });
        last = t;
      }
      raf = requestAnimationFrame(paint);
    };
    // ONE pending frame per sprite. The painting's ready callback runs at once when it is already loaded (the
    // usual case), and a second requestAnimationFrame here was a second loop that cleanup never cancelled: every
    // porch refetch leaked one per sprite, still lit after dawn (the 10-07 review).
    const kick = () => { if (!alive) return; if (raf) cancelAnimationFrame(raf); raf = requestAnimationFrame(paint); };
    const stop = loadPainting(kick);
    if (!raf) kick();
    return () => { alive = false; if (raf) cancelAnimationFrame(raf); raf = 0; stop(); };
  }, [item.pk, glow, index]);
  const { spot, pk, mine } = item;
  const ai = pk.kind === 'resident';
  const by = mine ? 'yours' : (pk.by_name || 'someone');
  return (
    <button
      ref={btnRef}
      type="button"
      className={`pk-sprite${ai ? ' pk-ai' : ''}${mine ? ' pk-mine' : ''}`}
      data-pumpkin-sprite={pk.id}
      aria-label={`${pk.name || 'A pumpkin'}, carved by ${mine ? 'you' : by}`}
      onClick={(e) => { e.stopPropagation(); if (onOpen) onOpen(pk, mine); }} // never also a tap on the picture under it (/look)
      style={{
        position: 'absolute', left: `${spot.x}%`, top: `${spot.y}%`, width: `${spot.w}%`, aspectRatio: '1 / 1',
        transform: 'translate(-50%, -88%)', padding: 0, border: 0, background: 'none', borderRadius: '50%',
        cursor: onOpen ? 'pointer' : 'default', overflow: 'visible', textShadow: 'none', boxShadow: 'none',
      }}
    >
      <canvas ref={ref} style={{ display: 'block', width: '100%', height: '100%' }} />
      <span
        className="pk-sprite-name"
        style={{
          // ABOVE the pumpkin: below it, the front spot's tag fell outside the scene's overflow:hidden (the 10-07 review)
          position: 'absolute', left: '50%', top: 4, transform: 'translate(-50%, -100%)', whiteSpace: 'nowrap',
          fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: 'rgba(12,9,22,.78)',
          color: ai ? AI : HUMAN, pointerEvents: 'none',
        }}
      >{by}</span>
    </button>
  );
}

/** The sprites layer: absolutely fills its (positioned) parent, the porch picture's box. */
export default function PorchPumpkins({ porch, roomId = 'porch', onOpen }) {
  const placed = useMemo(() => placePumpkins(porch, roomId), [porch, roomId]);
  const glow = (porch && porch.glow) || 'off';
  const [, setReady] = useState(0);
  useEffect(() => loadPainting(() => setReady((n) => n + 1)), []);
  if (!placed.length) return null;
  return (
    <div className="pk-sprites" data-porch-pumpkins={placed.length} style={{ position: 'absolute', inset: 0 }}>
      <style>{'.pk-sprite .pk-sprite-name{opacity:0;transition:opacity .15s ease}.pk-sprite:hover .pk-sprite-name,.pk-sprite:focus-visible .pk-sprite-name,.pk-sprite.pk-mine .pk-sprite-name{opacity:1}.pk-sprite:focus-visible{outline:2px solid #ffd76a}'}</style>
      {placed.map((item, i) => <Sprite key={item.pk.id} item={item} index={i} glow={glow} onOpen={onOpen} />)}
    </div>
  );
}
